import type { AppError } from '@aeon-karaoke/shared/generic/impl/error/AppError';
import type { IResult } from '@aeon-karaoke/shared/generic/interface/result/IResult';

/** Convenience type alias for Result with AppError default. */
export type Result<T, E extends AppError = AppError> = IResult<T, E>;

/** Utility functions for working with Result types. */
export const Result = {
  ok<T>(value: T): Result<T, never> {
    return { ok: true, value };
  },

  fail<E extends AppError>(error: E): Result<never, E> {
    return { ok: false, error };
  },

  map<T, U, E extends AppError>(
    result: Result<T, E>,
    fn: (value: T) => U
  ): Result<U, E> {
    return result.ok ? Result.ok(fn(result.value)) : result;
  },

  flatMap<T, U, E extends AppError>(
    result: Result<T, E>,
    fn: (value: T) => Result<U, E>
  ): Result<U, E> {
    return result.ok ? fn(result.value) : result;
  },

  isOk<T, E extends AppError>(result: Result<T, E>): result is { ok: true; value: T } {
    return result.ok;
  },

  isFail<T, E extends AppError>(result: Result<T, E>): result is { ok: false; error: E } {
    return !result.ok;
  },

  unwrap<T, E extends AppError>(result: Result<T, E>): T {
    if (result.ok) {
      return result.value;
    }
    throw result.error;
  },

  unwrapOr<T, E extends AppError>(result: Result<T, E>, defaultValue: T): T {
    return result.ok ? result.value : defaultValue;
  },
};
